import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FormField,
  FormSelect,
  FormTextarea,
} from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { useCreatePractitioner, useUpdatePractitioner } from "@/api/hooks/usePractitioners";
import { PAYMENT_LANE_LABELS, type PaymentLane, type Practitioner } from "@/api/types/practitioner";
import { ContactPicker } from "@/features/memberships/ContactPicker";

interface PractitionerFormDialogProps {
  open: boolean;
  onClose: () => void;
  practitioner?: Practitioner;
}

/**
 * Create/edit practitioner form. A practitioner's contact_id is immutable
 * after creation (no contact_id field on the PATCH schema), so the contact
 * picker only appears in create mode.
 */
export function PractitionerFormDialog({ open, onClose, practitioner }: PractitionerFormDialogProps) {
  const isEdit = !!practitioner;
  const createPractitioner = useCreatePractitioner();
  const updatePractitioner = useUpdatePractitioner();

  const [contactId, setContactId] = useState("");
  const [contactName, setContactName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [coiProviderName, setCoiProviderName] = useState("");
  const [coiPolicyNumber, setCoiPolicyNumber] = useState("");
  const [coiExpiryDate, setCoiExpiryDate] = useState("");
  const [paymentLane, setPaymentLane] = useState<PaymentLane>("rental");
  const [notes, setNotes] = useState("");
  const [active, setActive] = useState(true);

  useEffect(() => {
    if (!open) return;
    if (practitioner) {
      setContactId(practitioner.contact_id);
      setContactName(practitioner.contact_name);
      setBusinessName(practitioner.business_name ?? "");
      setCoiProviderName(practitioner.coi_provider_name ?? "");
      setCoiPolicyNumber(practitioner.coi_policy_number ?? "");
      setCoiExpiryDate(practitioner.coi_expiry_date ?? "");
      setPaymentLane(practitioner.payment_lane);
      setNotes(practitioner.notes ?? "");
      setActive(practitioner.active);
    } else {
      setContactId("");
      setContactName("");
      setBusinessName("");
      setCoiProviderName("");
      setCoiPolicyNumber("");
      setCoiExpiryDate("");
      setPaymentLane("rental");
      setNotes("");
      setActive(true);
    }
  }, [open, practitioner]);

  const isPending = createPractitioner.isPending || updatePractitioner.isPending;
  const canSubmit = (isEdit || !!contactId) && !isPending;

  async function handleSubmit() {
    if (!canSubmit) return;
    try {
      if (isEdit && practitioner) {
        await updatePractitioner.mutateAsync({
          id: practitioner.id,
          business_name: businessName.trim() || null,
          coi_provider_name: coiProviderName.trim() || null,
          coi_policy_number: coiPolicyNumber.trim() || null,
          coi_expiry_date: coiExpiryDate || null,
          payment_lane: paymentLane,
          active,
          notes: notes.trim() || null,
        });
        toastSuccess("Practitioner updated");
      } else {
        await createPractitioner.mutateAsync({
          contact_id: contactId,
          business_name: businessName.trim() || null,
          coi_provider_name: coiProviderName.trim() || null,
          coi_policy_number: coiPolicyNumber.trim() || null,
          coi_expiry_date: coiExpiryDate || null,
          payment_lane: paymentLane,
          notes: notes.trim() || null,
        });
        toastSuccess("Practitioner added");
      }
      onClose();
    } catch (err) {
      toastError(isEdit ? "Couldn't update practitioner" : "Couldn't add practitioner", getErrorMessage(err));
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel={isEdit ? "Edit practitioner" : "Add practitioner"}>
      <DialogContent size="md">
        <DialogHeader onClose={onClose}>{isEdit ? "Edit practitioner" : "Add practitioner"}</DialogHeader>
        <DialogBody className="space-y-4">
          {!isEdit && (
            <div>
              <label className="mb-1 block text-sm font-medium text-text-secondary">Contact</label>
              <ContactPicker value={contactId} onChange={(id, name) => { setContactId(id); setContactName(name); }} />
              {contactName && !contactId && (
                <p className="mt-1 text-xs text-danger">Select a contact from the list.</p>
              )}
            </div>
          )}

          <FormField
            label="Business name (optional)"
            type="text"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
          />

          <FormField
            label="COI provider name (optional)"
            type="text"
            value={coiProviderName}
            onChange={(e) => setCoiProviderName(e.target.value)}
          />

          <FormField
            label="COI policy number (optional)"
            type="text"
            value={coiPolicyNumber}
            onChange={(e) => setCoiPolicyNumber(e.target.value)}
          />

          <FormField
            label="COI expiry date (optional)"
            type="date"
            value={coiExpiryDate}
            onChange={(e) => setCoiExpiryDate(e.target.value)}
            data-testid="practitioner-coi-expiry-input"
          />

          <FormSelect
            label="Payment lane"
            value={paymentLane}
            onChange={(e) => setPaymentLane(e.target.value as PaymentLane)}
          >
            <option value="rental">{PAYMENT_LANE_LABELS.rental}</option>
            <option value="hosted_revshare">{PAYMENT_LANE_LABELS.hosted_revshare}</option>
          </FormSelect>

          <FormTextarea label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />

          {isEdit && (
            <Checkbox label="Active" checked={active} onChange={(e) => setActive(e.target.checked)} />
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit} data-testid="submit-practitioner-form">
            {isPending ? "Saving…" : isEdit ? "Save changes" : "Add practitioner"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
